import React, { useContext, useEffect, useState } from 'react';
import {
    useLoaderData,
    useNavigate,
    useParams,
    useSearchParams,
} from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import Location from '@r6digital/rapidstor-types/react/entities/Location';
import { MoveInPayload } from '@r6digital/rapidstor-types/types/Events/MoveIn';
import { ReservationEventPayloadStandard } from '@r6digital/rapidstor-types/types/Events/Reservation';
import { ReservationWithFee } from '@r6digital/rapidstor-types/types/Reservation';

import {
    useGetReservationQuery,
} from '@/core/api/rapidstor';
import { useMoveInSocket } from '@/core/hooks/useMoveInSocket';
import { useReservationFeeSocket } from '@/core/hooks/useReservationFeeSocket';
import Logger from '@/core/utils/logging';

import Alert from '@/core/components/elements/alert';
import Spinner from '@/core/components/elements/spinner';

import {
    moveInEvent,
    reservationWithFeeEvent,
} from '@/features/rapidstor/PostMessageEvents';
import { AnalyticsContext } from '@/features/rapidstor/providers/AnalyticsProvider';
import useRapidStorTracking from '../hooks/useRapidStorTracking';

type Type = 'autobill' | 'payment' | 'move-in' | 'reservation-with-fee';
type Token = string;

/**
 * The SiteLink fee receipt only exists once a reservation fee has actually been paid, so it lives
 * on `ReservationWithFee` rather than `BaseReservation`. On the error path the payment may have
 * failed and the receipt be absent, hence the optional read.
 */
const feeReceipt = (payload: ReservationEventPayloadStandard) => (
    payload.reservation as Partial<ReservationWithFee>
).reservationFeeAddWithSource;

export default function PaymentRedirect() {
    const location = useLoaderData() as Location;
    const navigate = useNavigate();
    const params = useParams();
    const [searchParams] = useSearchParams();
    const { firebaseLogEvent } = useContext(AnalyticsContext);
    const { trackEvent } = useRapidStorTracking();
    const { t } = useTranslation();
    const { startListening } = useMoveInSocket({ location });
    const {
        startListening: startReservationFeeListening,
    } = useReservationFeeSocket({ location });
    const [phase, setPhase] = useState<
    'redirecting' | 'awaiting-move-in' | 'awaiting-reservation-fee' | 'error'
    >('redirecting');
    const [errorMessage, setErrorMessage] = useState('');

    const token = params.token as Token;
    const type = params.type as Type;

    const {
        data: reservationData,
        isSuccess: reservationDataLoadedSuccessfully,
    } = useGetReservationQuery({
        locationCode: location.locationCode,
        corpCode: location.corpCode,
        token,
    }, { refetchOnMountOrArgChange: true });

    const rsV2Container = document.getElementById('rapidstor-v2-frontend');
    const spinner = document.getElementById('spinner');
    if (spinner && rsV2Container) {
    // rsV2Container.style.display = 'none';
    // spinner.style.display = 'block';
    }

    /**
   * Build move-in event payload
   *
   * @returns MoveInPayload | null
   */
    const buildMoveInEventPayload = (): MoveInPayload | null => {
        if (!reservationData || !reservationData.unitType) {
            return null;
        }
        const unitTypeRate = (reservationData?.rates.discounted
            ? reservationData?.rates.discountedRate
            : reservationData?.rates.rate) || 0;
        // A finalized reservation comes back without UnitID, sUnitName or moveInContext. The
        // amount charged is not available then, so the unit's rate stands in for it.
        const { unitToUse } = reservationData.unitType;

        return {
            deal_name: `${reservationData?.deal?.title}`,
            size: reservationData?.unitType.area,
            tenant: {
                firstName: reservationData?.firstName,
                lastName: reservationData?.lastName,
                email: reservationData?.email,
                phone: reservationData?.phone,
                tenantId: reservationData.tenantId,
            },
            transaction_id: reservationData.token,
            unit_id: reservationData?.UnitID ?? unitToUse?.UnitID,
            unit_length: reservationData?.unitType.length,
            unit_width: reservationData?.unitType.width,
            unit_name: reservationData?.sUnitName ?? unitToUse?.sUnitName,
            unit_rate: unitTypeRate,
            value: reservationData?.moveInContext?.movein?.paymentAmount ?? unitTypeRate,
            ledger_id: '',
            quoted_rate_monthly: location.rapidstor.showWeeklyRates
                ? unitTypeRate * 4
                : unitTypeRate,
            quoted_rate_weekly: location.rapidstor.showWeeklyRates
                ? unitTypeRate
                : unitTypeRate / 4,
            movein: {
                insuranceType:
          reservationData.moveInContext?.movein?.insuranceType || null,
                insuranceCoverage:
          reservationData.moveInContext?.movein?.insuranceCoverage || '',
                fileUploaded: false,
            },
            site_info: {
                location_code: location.locationCode,
                site_token:
          window.rapidstor_settings.rapidstor_options.locationToken || '',
            },
            reservation: reservationData,
        };
    };

    /**
   * Build reservation-with-fee event payload
   *
   * @returns ReservationEventPayloadStandard | null
   */
    const buildReservationWithFeeEventPayload = (): ReservationEventPayloadStandard | null => {
        if (!reservationData || !reservationData.unitType) {
            return null;
        }
        const unitTypeRate = (reservationData?.rates.discounted
            ? reservationData?.rates.discountedRate
            : reservationData?.rates.rate) || 0;

        return {
            quoted_rate_monthly: location.rapidstor.showWeeklyRates
                ? unitTypeRate * 4
                : unitTypeRate,
            quoted_rate_weekly: location.rapidstor.showWeeklyRates
                ? unitTypeRate
                : unitTypeRate / 4,
            size: reservationData?.unitType.area,
            transaction_id: reservationData.token,
            unit_id: reservationData?.UnitID,
            unit_length: reservationData?.unitType.length,
            unit_name: reservationData?.sUnitName,
            unit_rate: unitTypeRate,
            unit_width: reservationData?.unitType.width,
            site_info: {
                location_code: location.locationCode,
                site_token:
            window.rapidstor_settings.rapidstor_options.locationToken || '',
            },
            tenant: {
                firstName: reservationData?.firstName,
                lastName: reservationData?.lastName,
                email: reservationData?.email,
                phone: reservationData?.phone,
                tenantId: reservationData.tenantId,
            },
            value: unitTypeRate,
            reservation: reservationData,
            deal_name: `${reservationData?.deal?.title}`,
        };
    };

    // With R6Pay the gateway takes the payment and the API completes the transaction from
    // its webhook, so the outcome only reaches the browser over a socket. Other (non-gateway)
    // flows have already completed synchronously by the time we land here.
    const isR6Pay = !!(
        location.rapidstor.paymentGateways?.use
        && location.rapidstor.paymentGateways?.gateways?.r6Pay
    );

    useEffect(() => {
        const handleRedirect = () => {
            if (!token || !type) {
                Logger.warn('[PaymentRedirect] Missing token or type', undefined, [
                    {
                        name: 'token',
                        value: token,
                    },
                    {
                        name: 'type',
                        value: type,
                    },
                ]);
                navigate('/');
            }

            switch (type) {
                case 'autobill':
                case 'payment': {
                    if (searchParams.get('error') !== 'true') {
                        const completionRedirectUrl = location.rapidstor.jblRedirects.afterMoveIn.url;
                        const useCompletionRedirect = location.rapidstor.jblRedirects.afterMoveIn.enabled;

                        if (useCompletionRedirect && completionRedirectUrl) {
                            window.location.replace(completionRedirectUrl);
                        } else {
                            navigate(`/move-in-confirmed/${token}`);
                        }
                    } else {
                        navigate(
                            `/move-in-confirmed/${token}?error=true&message=${searchParams.get(
                                'message',
                            )}`,
                        );
                    }
                    break;
                }
                case 'move-in': {
                    const moveInPayload = buildMoveInEventPayload();
                    if (!moveInPayload) {
                        Logger.warn('[PaymentRedirect] Missing move-in payload');
                        return;
                    }

                    const completeMoveInSuccess = () => {
                        moveInEvent(moveInPayload);

                        trackEvent('move-in', {
                            unit_name: moveInPayload.unit_name,
                            unit_length: String(moveInPayload.unit_length),
                            unit_width: String(moveInPayload.unit_width),
                            unit_rate: String(moveInPayload.unit_rate),
                            value: String(moveInPayload.value),
                            currency: location.rapidstor.currency,
                        });
                        firebaseLogEvent('move_in_success', {});
                        firebaseLogEvent('purchase', {
                            currency: location.rapidstor.currency,
                            value: `${moveInPayload.value}`,
                        });

                        const completionRedirectUrl = location.rapidstor.jblRedirects.afterMoveIn.url;
                        const useCompletionRedirect = location.rapidstor.jblRedirects.afterMoveIn.enabled;

                        if (useCompletionRedirect && completionRedirectUrl) {
                            window.location.replace(completionRedirectUrl);
                        } else {
                            navigate(`/move-in-confirmed/${token}`);
                        }
                    };

                    if (searchParams.get('error') !== 'true') {
                        // R6Pay performs the move-in server-side (via its webhook) after taking
                        // payment, so wait for the moveInEvent socket before confirming. If the
                        // webhook already finalized the reservation before this page loaded, the
                        // socket event has been and gone, so confirm straight away.
                        const isAlreadyFinalized = !!reservationData
                            && 'finalized' in reservationData
                            && reservationData.finalized;
                        if (isR6Pay && reservationData?._id && !isAlreadyFinalized) {
                            setPhase('awaiting-move-in');
                            startListening(reservationData._id, {
                                onSuccess: completeMoveInSuccess,
                                onFailure: () => {
                                    setErrorMessage(t('wizard:moveInPaymentSucceededButFailed'));
                                    setPhase('error');
                                },
                                onTimeout: () => {
                                    setErrorMessage(t('wizard:moveInProcessingTimeout'));
                                    setPhase('error');
                                },
                            });
                        } else {
                            completeMoveInSuccess();
                        }
                    } else {
                        trackEvent('move-in-error', {
                            error: searchParams.get('message') || '',
                            error_code: searchParams.get('code') || '',
                            currency: location.rapidstor.currency,
                            value: String(moveInPayload.value),
                            unit_name: moveInPayload.unit_name,
                            unit_length: String(moveInPayload.unit_length),
                            unit_width: String(moveInPayload.unit_width),
                            unit_rate: String(moveInPayload.unit_rate),
                            tenant_id: String(moveInPayload.tenant?.tenantId),
                        });

                        navigate(
                            `/move-in/${token}?error=true&message=${searchParams.get(
                                'message',
                            )}`,
                        );
                    }

                    break;
                }
                case 'reservation-with-fee': {
                    const reservationWithFeePayload = buildReservationWithFeeEventPayload();
                    if (!reservationWithFeePayload) {
                        Logger.warn(
                            '[PaymentRedirect] Missing reservation-with-fee payload',
                        );
                        return;
                    }

                    const completeReservationWithFeeSuccess = () => {
                        reservationWithFeeEvent(reservationWithFeePayload);
                        trackEvent('reservation-with-fee', {
                            unit_name: reservationWithFeePayload.unit_name,
                            unit_length: String(reservationWithFeePayload.unit_length),
                            unit_width: String(reservationWithFeePayload.unit_width),
                            unit_rate: String(reservationWithFeePayload.unit_rate),
                            value: String(feeReceipt(reservationWithFeePayload)?.dcAmt ?? ''),
                            currency: location.rapidstor.currency,
                        });

                        const paidReservationRedirectUrl = location.rapidstor.jblRedirects.afterPaidReservation.url;
                        const usePaidReservationRedirect = location.rapidstor.jblRedirects.afterPaidReservation.enabled;

                        if (usePaidReservationRedirect && paidReservationRedirectUrl) {
                            window.location.replace(paidReservationRedirectUrl);
                        } else {
                            navigate(`/reservation-with-fee/${token}/return`);
                        }
                    };

                    if (searchParams.get('error') !== 'true') {
                        // R6Pay adds the fee to the reservation server-side (via its webhook)
                        // after taking payment, so wait for the reservationFeeEvent socket
                        // before confirming — otherwise the customer is told the reservation
                        // is paid before the fee has actually been charged, and any failure
                        // downstream is never surfaced to them.
                        if (isR6Pay) {
                            if (!reservationData?._id) {
                                // The room is keyed by reservation id, so without one the fee's
                                // outcome is unknowable. Confirming anyway is the exact failure
                                // this wait exists to prevent, so send them to the store instead.
                                Logger.error(
                                    '[PaymentRedirect] No reservation id — cannot await the reservation fee',
                                    location,
                                    [{ name: 'rapidstor_reservationToken', value: token }],
                                );
                                setErrorMessage(t('wizard:reservationFeePaymentSucceededButFailed'));
                                setPhase('error');
                                return;
                            }
                            setPhase('awaiting-reservation-fee');
                            startReservationFeeListening(reservationData._id, {
                                onSuccess: completeReservationWithFeeSuccess,
                                onFailure: () => {
                                    setErrorMessage(t('wizard:reservationFeePaymentSucceededButFailed'));
                                    setPhase('error');
                                },
                                onTimeout: () => {
                                    setErrorMessage(t('wizard:reservationFeeProcessingTimeout'));
                                    setPhase('error');
                                },
                            });
                        } else {
                            completeReservationWithFeeSuccess();
                        }
                    } else {
                        trackEvent('reservation-with-fee-error', {
                            error: searchParams.get('message') || '',
                            error_code: searchParams.get('code') || '',
                            currency: location.rapidstor.currency,
                            value: String(feeReceipt(reservationWithFeePayload)?.dcAmt ?? ''),
                            unit_name: reservationWithFeePayload.unit_name,
                            unit_length: String(reservationWithFeePayload.unit_length),
                            unit_width: String(reservationWithFeePayload.unit_width),
                            unit_rate: String(reservationWithFeePayload.unit_rate),
                            tenant_id: String(reservationWithFeePayload.tenant?.tenantId),
                        });

                        navigate(
                            `/reservation-with-fee/${token}?error=true&message=${searchParams.get(
                                'message',
                            )}`,
                        );
                    }
                    break;
                }
                default:
                    Logger.warn('[PaymentRedirect] Invalid type', undefined, [
                        {
                            name: 'type',
                            value: type,
                        },
                    ]);
                    navigate('/');
                    break;
            }
        };

        if (reservationDataLoadedSuccessfully) {
            handleRedirect();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [reservationDataLoadedSuccessfully]);

    if (phase === 'awaiting-move-in' || phase === 'awaiting-reservation-fee') {
        return (
            <div
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '16px',
                    padding: '48px',
                    minHeight: '40vh',
                    textAlign: 'center',
                }}
            >
                <Spinner size="40" useDataAttribute />
                <p>
                    {phase === 'awaiting-move-in'
                        ? t('wizard:finalisingMoveIn')
                        : t('wizard:finalisingReservation')}
                </p>
            </div>
        );
    }

    if (phase === 'error') {
        const contactAddress = [
            location.address.address1,
            location.address.city,
            location.address.region,
        ].filter(Boolean).join(', ');
        // The move-in form details live in moveInContext.tenant; the top-level reservation
        // fields keep their creation-time defaults (e.g. lastName '.'), so prefer the
        // move-in tenant and fall back to the top-level fields.
        const moveInTenant = reservationData?.moveInContext?.tenant;
        const rawLastName = moveInTenant?.lastName ?? reservationData?.lastName ?? '';
        const tenantLastName = rawLastName === '.' ? '' : rawLastName;
        const tenantFirstName = moveInTenant?.firstName ?? reservationData?.firstName ?? '';
        const customerName = `${tenantFirstName} ${tenantLastName}`.trim();
        const tenantPhone = moveInTenant?.phone ?? reservationData?.phone ?? '';
        const tenantEmail = moveInTenant?.email ?? reservationData?.email ?? '';
        const unitName = reservationData?.unitType?.unitToUse?.sUnitName ?? '';

        return (
            <div
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '24px',
                    padding: '48px',
                    minHeight: '40vh',
                    maxWidth: '560px',
                    margin: '0 auto',
                    textAlign: 'center',
                }}
            >
                <Alert type="error" text={errorMessage} />

                <div>
                    <p><strong>{t('wizard:moveInErrorContactHeading')}</strong></p>
                    <p>{location.name}</p>
                    {contactAddress ? <p>{contactAddress}</p> : null}
                    {location.contactPhone
                        ? <p>{t('wizard:moveInErrorPhone', { phone: location.contactPhone })}</p>
                        : null}
                    {location.contactEmail
                        ? <p>{t('wizard:moveInErrorEmail', { email: location.contactEmail })}</p>
                        : null}
                </div>

                <div>
                    <p><strong>{t('wizard:moveInErrorQuoteHeading')}</strong></p>
                    {customerName ? <p>{t('wizard:moveInErrorName', { name: customerName })}</p> : null}
                    {tenantPhone
                        ? <p>{t('wizard:moveInErrorPhone', { phone: tenantPhone })}</p>
                        : null}
                    {tenantEmail
                        ? <p>{t('wizard:moveInErrorEmail', { email: tenantEmail })}</p>
                        : null}
                    {unitName ? <p>{t('wizard:moveInErrorUnit', { unit: unitName })}</p> : null}
                </div>
            </div>
        );
    }

    return (
        <div
            style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '16px',
                padding: '48px',
                minHeight: '40vh',
                textAlign: 'center',
            }}
        >
            <Spinner size="40" useDataAttribute />
        </div>
    );
}
